import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { OfficeEditFormValues, OfficePhoneRow } from "services/api/offices/types";
import { getOfficePhonesAPI, deleteOfficePhoneAPI } from "services/api/offices/api";
import { initialDataWithPagination } from "services/api/consts";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";
import OfficePhoneEditCard from "./OfficePhoneEditCard/OfficePhoneEditCard";
import { OFFICE_PHONE_COLUMNS } from "./consts";

interface OfficePhoneStepProps {
  formValues: OfficeEditFormValues;
}

const OfficePhoneStep: React.FC<OfficePhoneStepProps> = ({ formValues: parentFormValues }) => {
  const officeId = parentFormValues.id;
  const { t } = useTranslate();
  const [phones, setPhones] = useState<OfficePhoneRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editPhoneId, setEditPhoneId] = useState<string | null>(null);
  const [deletePhoneId, setDeletePhoneId] = useState<string | null>(null);

  const fetchPhones = useCallback(async () => {
    if (!officeId) return;
    setIsLoading(true);
    try {
      const result = await getOfficePhonesAPI(initialDataWithPagination.pagination, officeId);
      setPhones(result.data);
    } catch {
      setPhones([]);
    }
    setIsLoading(false);
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchPhones();
  }, [officeId]);

  const handleRowClick = useCallback((phoneId: string) => {
    setEditPhoneId(phoneId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditPhoneId(null);
    setShowEdit(true);
  };

  // Klik na koš ne smije otvoriti i uređivanje retka ispod njega.
  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, phoneId: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeletePhoneId(phoneId);
    },
    []
  );

  const handleDeleteConfirm = async () => {
    if (!officeId || !deletePhoneId) return;
    await deleteOfficePhoneAPI(t, officeId, deletePhoneId);
    setDeletePhoneId(null);
    fetchPhones();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditPhoneId(null);
    fetchPhones();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Telefoni poslovnice</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte telefonske brojeve poslovnice klikom na gumb <strong>"Dodaj telefon"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {/* Uređivanje ide klikom na redak; prazno stanje nudi samo unos. */}
          {!isLoading && phones.length === 0 ? (
            <div className="max-w-md mx-auto">
              <Button
                variant="primary"
                size="full"
                startIcon="/media/icons/plus.svg"
                children="Dodaj telefon"
                onClick={handleAddNew}
              />
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <Button
                  variant="primary"
                  startIcon="/media/icons/plus.svg"
                  children="Dodaj telefon"
                  onClick={handleAddNew}
                />
              </div>

              <TableSearch
                columns={OFFICE_PHONE_COLUMNS(handleDeleteClick)}
                data={phones}
                selectableRows={false}
                onRowClick={handleRowClick}
                isLoading={isLoading}
                disablePagination
              />
            </>
          )}
        </div>
      </div>

      <OfficePhoneEditCard
        officeId={officeId ?? ""}
        phoneId={editPhoneId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
      <Dialog
        open={!!deletePhoneId}
        title="Brisanje telefona"
        message="Jeste li sigurni da želite obrisati ovaj telefon?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletePhoneId(null)}
      />
    </>
  );
};

export default memo(OfficePhoneStep);
